import Vue from "vue";
import Vuex from "vuex";
import localStore from "store";
import { USER_ROLES } from "@/services/constants";
import organizationService from "@/services/organization";
import { hasCapabilityIn } from "@/services/capabilities";

const {
  ADMIN,
  MANAGER,
  DOCTOR,
  ASSISTANT,
  PATIENT,
  ADMISSIONIST,
  SECRETARY,
  HEALTH_ADMIN,
} = USER_ROLES;

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    token: localStore.get("token") || window.accessToken || "",
    user: localStore.get("user") || window.user || {},
    // UC-REG-067 + UC-REG-068. Branding + flags de la org del user actual.
    // Se hidrata via action `loadOrganizationContext` al login y se aplica
    // en runtime al header del Dashboard + CSS vars (override del tenant).
    organizationContext: localStore.get("organizationContext") || null,
    cacheDiagnoses: {},
    cacheImages: {},
    cacheLabs: {},
    // Cantidad de tareas pendientes del paciente (para el globito del sidebar).
    // Lo setean la card "Mi día" (/patient/me) y la vista Mis tareas.
    patientPendingTasks: 0,
    cacheMedications: {},
    cacheLabRoutines: {},
    cachePrograms: {},
  },

  getters: {
    user({ user }) {
      return user;
    },

    role({ user }) {
      return user.role;
    },

    isDoctor({ user }) {
      return user.role === DOCTOR.name || user.role === DOCTOR.alias;
    },

    isAdmin({ user }) {
      return user.role === ADMIN.name || user.role === ADMIN.alias;
    },

    isAssistant({ user }) {
      return user.role === ASSISTANT.name || user.role === ASSISTANT.alias;
    },

    isManager({ user }) {
      return user.role === MANAGER.name || user.role === MANAGER.alias;
    },

    isPatient({ user }) {
      return user.role === PATIENT.name || user.role === PATIENT.alias;
    },

    isAdmissionist({ user }) {
      return (
        user.role === ADMISSIONIST.name || user.role === ADMISSIONIST.alias
      );
    },

    /** @description Indica si el usuario es secretaria de prestador */
    isSecretary({ user }) {
      return user.role === SECRETARY.name || user.role === SECRETARY.alias;
    },

    /** @description Indica si el usuario es administrativo del financiador */
    isHealthAdmin({ user }) {
      return (
        user.role === HEALTH_ADMIN.name || user.role === HEALTH_ADMIN.alias
      );
    },

    /**
     * @description Indica si el usuario es staff de Quantux con acceso al
     *              tablero de Plataforma. Se basa en `User.type === 'platform-admin'`
     *              (no en el Role). Solo este getter habilita el menú "Plataforma"
     *              en la sidebar — admins del tenant cliente NO lo ven.
     */
    isPlatformAdmin({ user }) {
      return user.type === "platform-admin";
    },

    /** @description Indica si el usuario doctor tiene gestión clínica habilitada. Siempre true para no-doctores */
    hasClinicalAccess({ user }) {
      const isDoctor = user.role === DOCTOR.name || user.role === DOCTOR.alias;
      if (!isDoctor) return true;
      return user.doctor?.clinicalEnabled !== false;
    },

    getDiagnosisById: (state) => (id) => {
      return state.cacheDiagnoses[id] || null;
    },

    getImageById: (state) => (id) => {
      return state.cacheImages[id] || null;
    },

    getLabById: (state) => (id) => {
      return state.cacheLabs[id] || null;
    },

    getMedicationById: (state) => (id) => {
      return state.cacheMedications[id] || null;
    },

    getLabRoutineById: (state) => (id) => {
      return state.cacheLabRoutines[id] || null;
    },

    getProgramById: (state) => (id) => {
      return state.cachePrograms[id] || null;
    },

    getAllMedications: (state) => Object.values(state.cacheMedications),
    /**
     * @description UC-REG-067 + UC-REG-068. Branding efectivo aplicable
     *              al dashboard. Si la org tiene branding propio, lo devuelve;
     *              si no, devuelve {} para que el dashboard caiga al default
     *              del tenant (env vars del build).
     */
    orgBranding({ organizationContext }) {
      return (organizationContext && organizationContext.branding) || {};
    },

    /**
     * @description UC-REG-067. Indica si la org tiene "modo policonsultorio
     *              compartido" activado. Lo usan componentes para mostrar
     *              tooltips/copy distinto.
     */
    sharePatientsAcrossDoctors({ organizationContext }) {
      return !!(
        organizationContext && organizationContext.sharePatientsAcrossDoctors
      );
    },

    /**
     * @description UC-REG-314. Capacidades del tenant resueltas por la API
     *              (`GET /organization/me` → `capabilities`, perfil por
     *              código en `config/tenant-profiles.js`). Sin contexto o sin
     *              la clave: false. La API hace cumplir la regla; el
     *              Dashboard solo muestra u oculta.
     * @returns {(key: string) => boolean}
     */
    hasCapability({ organizationContext }) {
      return (key) => hasCapabilityIn(organizationContext, key);
    },

    getAllImages: (state) => Object.values(state.cacheImages),
    getAllLabs: (state) => Object.values(state.cacheLabs),
    getAllLabRoutines: (state) => Object.values(state.cacheLabRoutines),
    getAllPrograms: (state) => Object.values(state.cachePrograms),
    allDiagnoses: (state) => Object.values(state.cacheDiagnoses),
  },

  mutations: {
    setToken(state, token) {
      state.token = token;
      localStore.set("token", token);
    },
    /**
     * @description Setea la cantidad de tareas pendientes del paciente
     *              (globito del sidebar).
     * @param {Object} state
     * @param {number} n
     */
    setPatientPendingTasks(state, n) {
      state.patientPendingTasks = Number(n) || 0;
    },
    setUser(state, user) {
      state.user = user;
      localStore.set("user", user);
    },
    /**
     * @description UC-REG-067 + UC-REG-068. Setea el contexto de la org
     *              actual (branding + features + flags). Lo persiste en
     *              localStorage para sobrevivir refresh y NO esperar el
     *              roundtrip al backend en cada navegación.
     */
    setOrganizationContext(state, ctx) {
      state.organizationContext = ctx;
      localStore.set("organizationContext", ctx);
    },
    clearOrganizationContext(state) {
      state.organizationContext = null;
      localStore.remove("organizationContext");
    },

    ADD_DIAGNOSES(state, diagnoses) {
      const { _id, description, featured = false } = diagnoses;
      if (!state.cacheDiagnoses[_id]) {
        state.cacheDiagnoses[_id] = { _id, description, featured };
      }
    },

    ADD_IMAGES(state, images) {
      const { _id, code, description, category } = images;
      if (!state.cacheImages[_id]) {
        state.cacheImages[_id] = { _id, code, description, category };
      }
    },
    ADD_LABS(state, labs) {
      const { _id, code, description, routine } = labs;
      if (!state.cacheLabs[_id]) {
        state.cacheLabs[_id] = { _id, code, description, routine };
      }
    },

    ADD_MEDICATION(state, medication) {
      const { _id, name, presentation, brandName, drug } = medication;
      if (!state.cacheMedications[_id]) {
        state.cacheMedications[_id] = {
          _id,
          name,
          presentation,
          brandName,
          drug,
        };
      }
    },

    ADD_LAB_ROUTINE(state, routine) {
      const { _id, name, description, code } = routine;
      if (!state.cacheLabRoutines[_id]) {
        state.cacheLabRoutines[_id] = { _id, name, description, code };
      }
    },

    ADD_PROGRAM(state, program) {
      const { _id, name, description } = program;
      if (!state.cachePrograms[_id]) {
        state.cachePrograms[_id] = { _id, name, description };
      }
    },

    SET_MEDICATIONS(state, medications) {
      const reduced = {};
      medications.forEach(({ _id, name, presentation }) => {
        reduced[_id] = { _id, name, presentation };
      });
      state.cacheMedications = reduced;
    },

    SET_DIAGNOSES(state, diagnoses) {
      const reduced = {};
      diagnoses.forEach(({ _id, description, featured = false }) => {
        reduced[_id] = { _id, description, featured };
      });
      state.cacheDiagnoses = reduced;
    },

    SET_IMAGES(state, images) {
      const reduced = {};
      images.forEach(({ _id, code, description, category }) => {
        reduced[_id] = { _id, code, description, category };
      });
      state.cacheImages = reduced;
    },

    SET_LABS(state, labs) {
      const reduced = {};
      labs.forEach(({ _id, code, description, routine }) => {
        reduced[_id] = { _id, code, description, routine };
      });
      state.cacheLabs = reduced;
    },

    SET_LAB_ROUTINES(state, routines) {
      const reduced = {};
      routines.forEach(({ _id, name, description, code }) => {
        reduced[_id] = { _id, name, description, code };
      });
      state.cacheLabRoutines = reduced;
    },

    SET_PROGRAMS(state, programs) {
      const reduced = {};
      programs.forEach(({ _id, name, description }) => {
        reduced[_id] = { _id, name, description };
      });
      state.cachePrograms = reduced;
    },

    CLEAR_LAB_ROUTINES_CACHE(state) {
      state.cacheLabRoutines = {};
    },
  },

  actions: {
    /**
     * @description UC-REG-067 + UC-REG-068. Carga el contexto de la org del
     *              user autenticado (branding, features, flag de share).
     *              Llamar al login y al refresh inicial de la SPA. Falla
     *              silenciosa si no se puede traer — el dashboard cae al
     *              branding del tenant (env vars del build).
     */
    async loadOrganizationContext({ commit }) {
      try {
        const ctx = await organizationService.getMe();
        commit("setOrganizationContext", ctx);
        return ctx;
      } catch (e) {
        // Mantenemos el cache previo (si hay) y log silente. El branding
        // del tenant aplica de fallback al no haber ctx.
        return null;
      }
    },
    addDiagnoses({ commit }, diagnoses) {
      commit("ADD_DIAGNOSES", diagnoses);
    },
    addImages({ commit }, images) {
      commit("ADD_IMAGES", images);
    },
    addLabs({ commit }, labs) {
      commit("ADD_LABS", labs);
    },
    addMedication({ commit }, medication) {
      commit("ADD_MEDICATION", medication);
    },
    addLabRoutine({ commit }, routine) {
      commit("ADD_LAB_ROUTINE", routine);
    },
    addProgram({ commit }, program) {
      commit("ADD_PROGRAM", program);
    },
  },
});
